import { useState } from "react";
import type { ReasonPayload } from "@/services/reasonCodes";
import { ReasonCancelled, isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Check, X } from "lucide-react";
import { listChannelConfig, marginOfMarkup, updateChannelConfig } from "@/services/pricing";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { Card } from "@/shared/ui/Card";
import { useCan } from "@/auth/permissions";
import { PricingSubnav } from "./PricingSubnav";

const INP = "h-8 w-16 rounded-md border border-slate-300 px-2 text-[13px] focus:border-blue-500 focus:outline-none";

/** canvas 静态描述符（加价方式/含税/促销/定价模式）· 成本加成率来自 DB 可编辑。 */
const DESC: Record<string, { markup: string; commission: string; tax: string; promo: string; mode: string }> = {
  b2b: { markup: "同行分级加价", commission: "无", tax: "否", promo: "否", mode: "固定价目表" },
  direct: { markup: "零售（成本加成）", commission: "无", tax: "是", promo: "可下探", mode: "实时动态" },
  social: { markup: "零售 + 服务费", commission: "无（自营流量）", tax: "是", promo: "活动价", mode: "实时动态" },
  api: { markup: "平台加价", commission: "抽佣 15–20%", tax: "平台", promo: "平台活动", mode: "平台同步价" },
};
/** 填了 33.33 → 「≈ 毛利率 25%」（只读，随输入实时变）。 */
const MarginHint = ({ markup }: { markup: string }) => {
  const n = Number(markup);
  return <div className="mt-0.5 text-[11px] font-normal text-slate-400" data-testid="margin-hint">{markup.trim() === "" || Number.isNaN(n) ? "≈ 毛利率 —" : `≈ 毛利率 ${+marginOfMarkup(n).toFixed(2)}%`}</div>;
};
const CH_CN: Record<string, string> = { b2b: "同行 B2B", direct: "客户端散客", social: "社媒平台", api: "API 平台" };

/** 售价管理中心 · 渠道与系数配置（/admin/pricing/channels · canvas page-62 · 口径⑧）。 */
export function ChannelConfigPage() {
  const qc = useQueryClient();
  const canEdit = useCan("pricing.channel_config.update");
  const seesMarkup = useCan("pricing.cost.view");   // RBACFIX-2 · 成本加成率 / 毛利率只给成本查看权限（后端对其他人返回 null）；佣金率 / 同行建议加价率照旧
  const q = useQuery({ queryKey: ["pricing", "channel-config"], queryFn: listChannelConfig });
  // c = 这一列（库里 commission_pct）：同行 b2b = 同行建议加价率（建议零售价 = 结算价 × (1 + 它)，MARKUP-1b）；API = 平台佣金率（挂牌价 = 结算价 ÷ (1 − 它)）。原先这一列是写死的描述文字（API 平台显示「抽佣 15–20%」而库里是 18%），且改不了——
  // 09-24 GYG 实际抽 25% 时老板没处改，只能去改加成率，两格叠加把价抬高 ~18%。现显示真值并可改。
  const [edit, setEdit] = useState<{ channel: string; m: string; c: string } | null>(null);

  const { ask: askReason, dialog: reasonDialog } = useReasonDialog();   // REASON-1
  const save = useMutation({
    mutationFn: async () => {
      // REASON-1 · 加成率 / 佣金真的变了才问原因（改前 → 改后）
      const cur = (q.data ?? []).find((c) => c.channel === edit!.channel);
      const changes = [];
      if (seesMarkup && cur && cur.markup_pct != null && Number(edit!.m) !== cur.markup_pct) changes.push({ label: "成本加成率", before: `${cur.markup_pct}%`, after: `${Number(edit!.m)}%` });
      if (cur && Number(edit!.c) !== cur.commission_pct) changes.push({ label: "同行建议加价率 / 平台佣金率", before: `${cur.commission_pct}%`, after: `${Number(edit!.c)}%` });
      let r: ReasonPayload = {};
      if (changes.length) {
        const got = await askReason({ scene: "price_change", title: "调整渠道系数", subtitle: CH_CN[edit!.channel] ?? edit!.channel, changes, confirmText: "确认并保存" });
        if (!got) throw new ReasonCancelled();
        r = got;
      }
      return updateChannelConfig(edit!.channel, { ...(seesMarkup ? { markup_pct: Number(edit!.m) } : {}), commission_pct: Number(edit!.c), ...r });
    },
    onSuccess: () => { setEdit(null); qc.invalidateQueries({ queryKey: ["pricing", "channel-config"] }); },
    onError: (e) => { if (!isReasonCancelled(e)) alert((e as Error).message || "保存失败"); },
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError)
    return (
      <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500">
        <span>渠道配置加载失败</span>
        <button onClick={() => q.refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
      </div>
    );

  return (
    <PageShell title="渠道与系数配置" subtitle="成本引擎全渠道共享 · 每渠道挂自己的成本加成率 / 同行建议加价率（同行）· 平台佣金率（API）+ 定价模式">
      <PricingSubnav />
      <div className="text-[12px] text-slate-400">售价管理中心 › 渠道与系数配置</div>

      <div className="rounded-xl bg-slate-900 px-5 py-4 text-white">
        {seesMarkup && <div className="font-mono text-sm font-bold">结算价 = 成本 × (1 + 成本加成率) × 节假日系数</div>}
        <div className="mt-1 font-mono text-[12.5px] text-slate-200">同行建议零售价 = 结算价 × (1 + 同行建议加价率)；API 平台价 = 结算价 ÷ (1 − 平台佣金率)</div>
        <div className="mt-1 text-xs text-slate-400">成本引擎只算一次成本 · 各渠道套不同的成本加成率 / 佣金系数出价 · 毛利率只作显示：(价 − 成本) ÷ 价</div>
      </div>

      <Card title="4 渠道系数" action={!canEdit ? <span className="text-[11px] text-slate-400">🔒 仅老板/财务/超管可改</span> : <span className="text-[11px] text-slate-400">点 ✎ 改佣金 / 成本加成率</span>}>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full min-w-[760px] text-[13px]">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">渠道</th>
                <th className="px-4 py-3 text-left">加价方式</th>
                <th className="px-4 py-3 text-left" title="同行：同行建议加价率（在结算价上加价给同行赚）；API 平台：平台佣金率（按售价抽成）">同行建议加价率 / 平台佣金率</th>
                <th className="px-4 py-3 text-left">含税</th>
                <th className="px-4 py-3 text-left">促销</th>
                <th className="px-4 py-3 text-left">定价模式</th>
                {seesMarkup && <th className="px-4 py-3 text-left">成本加成率</th>}
                {canEdit && <th className="px-4 py-3 text-right">操作</th>}
              </tr>
            </thead>
            <tbody>
              {q.data?.map((ch) => {
                const d = DESC[ch.channel] ?? { markup: "—", commission: `${ch.commission_pct}%`, tax: "—", promo: "—", mode: "—" };
                const e = edit?.channel === ch.channel ? edit : null;
                return (
                  <tr key={ch.channel} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium text-slate-800">{CH_CN[ch.channel] ?? ch.label}</td>
                    <td className="px-4 py-3 text-slate-600">{d.markup}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {e ? (
                        <input className={INP} value={e.c} onChange={(ev) => setEdit({ ...e, c: ev.target.value })} />
                      ) : ch.commission_pct > 0 ? (
                        <span className="font-semibold text-slate-900">{ch.commission_pct}%</span>
                      ) : (
                        d.commission
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{d.tax}</td>
                    <td className="px-4 py-3 text-slate-600">{d.promo}</td>
                    <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">{d.mode}</span></td>
                    {seesMarkup && (
                      <td className="px-4 py-3">
                        {e ? <input className={INP} value={e.m} onChange={(ev) => setEdit({ ...e, m: ev.target.value })} data-testid="markup-input" /> : <span className="font-semibold text-slate-900">{ch.markup_pct == null ? "—" : `${ch.markup_pct}%`}</span>}
                        <MarginHint markup={e ? e.m : ch.markup_pct == null ? "" : String(ch.markup_pct)} />
                      </td>
                    )}
                    {canEdit && (
                      <td className="px-4 py-3 text-right">
                        {e ? (
                          <div className="flex justify-end gap-1.5">
                            <button disabled={save.isPending} onClick={() => save.mutate()} className="rounded-md border border-emerald-300 p-1.5 text-emerald-600 hover:bg-emerald-50"><Check className="h-3.5 w-3.5" /></button>
                            <button onClick={() => setEdit(null)} className="rounded-md border border-slate-300 p-1.5 text-slate-500 hover:bg-slate-50"><X className="h-3.5 w-3.5" /></button>
                          </div>
                        ) : (
                          <button onClick={() => setEdit({ channel: ch.channel, m: ch.markup_pct == null ? "" : String(ch.markup_pct), c: String(ch.commission_pct) })} className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50"><Pencil className="h-3.5 w-3.5" /></button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {seesMarkup ? (
          <div className="mt-2 text-[11px] text-slate-400">💡 同行建议加价率 {q.data?.find((c) => c.channel === "b2b")?.commission_pct ?? "—"}%（建议零售价 = 结算价 × (1 + 加价率)；成本加成 {q.data?.find((c) => c.channel === "b2b")?.markup_pct ?? "—"}%）· 散客无佣金（成本加成 {q.data?.find((c) => c.channel === "direct")?.markup_pct ?? "—"}%）· 改成本加成率即影响全产品报价（只想调某一个产品：售价总览里点该产品的「调整系数」）。API 平台：佣金填平台实际抽成（如 GYG 25%），成本加成率填我方加价——两格叠加：结算价 = 成本 × (1 + 加成率)，挂牌价 = 结算价 ÷ (1 − 平台佣金率)。</div>
        ) : (
          <div className="mt-2 text-[11px] text-slate-400">💡 同行建议加价率 {q.data?.find((c) => c.channel === "b2b")?.commission_pct ?? "—"}%（建议零售价 = 结算价 × (1 + 加价率)）· 散客无佣金 · API 平台价 = 结算价 ÷ (1 − 平台佣金率)。成本加成率仅持有「成本查看」权限的角色可见。</div>
        )}
      </Card>

      <Card title="定价模式演进">
        <p className="text-[13px] text-slate-600">同行现阶段用「固定价目表」全年统一价 · 等同行习惯系统后可切「实时动态」（改 pricing_mode 开关 · 引擎不动）。</p>
      </Card>
    {reasonDialog}
    </PageShell>
  );
}
